import React, { createContext, useReducer, useEffect, useContext } from 'react';
import { node, string, bool } from 'prop-types';
import _ from 'lodash'; // eslint-disable-line

import {
  extend,
  params,
  string as stringType,
  shape as shapeType,
  arrayOf,
  useDataModel
} from '@thd-nucleus/data-sources';

import { RentalEstimateWrapperProvider } from '@thd-olt-component-react/rental-estimate';

import { reducer } from '../reducers/reducer';
import { FeatureSwitchContext } from './feature-switch-context';
import {
  GET_RENTAL_FDAV1_MODIFYING_QUERY,
  GET_RENTAL_FDAV1_QUERY,
  GET_RENTAL_FDAV4_QUERY,
  GET_RENTAL_RESERVATION_QUERY,
  SUBMIT_RENTAL_RESERVATION_MODIFICATION_QUERY
} from '../utils/queries';

export const defaultContext = {
  activeSection: 'DaysAndTimes',
  availableNearby: true,
  nearbyWhitelistedStoresList: [],
  reservation: {
    pickupDate: '',
    pickupTime: '',
    dropoffDate: '',
    dropoffTime: ''
  },
  reservationModifyStore: {},
  selectedStore: {},
  selectedAvailability: {
    start: '',
    end: ''
  },
  contractTerms: null,
  selectedStoreAvailability: true,
  editStore: false,
  validTimeOption: true,
  timeErrorMessage: '',
  damageProtection: true
};

export const ReservationContext = createContext(defaultContext);

const ReservationProvider = ({
  reservationId,
  uuid,
  storeId,
  children
}) => {

  const storeSearchOptions = {
    skip: !storeId,
    ssr: true,
    variables: {
      storeSearchInput: storeId,
      pagesize: '1'
    },
  };

  const { data } = useDataModel('storeSearch', storeSearchOptions);
  const store = data?.storeSearch?.stores[0] || {};

  const { damageProtectionEnabled } = useContext(FeatureSwitchContext);

  const [state, dispatch] = useReducer(reducer, {
    ...defaultContext,
    storeId,
    reservationId,
    uuid
  });

  useEffect(() => {
    if (!_.isEmpty(store)) {
      dispatch({
        type: 'SET_NEARBY_WHITELISTED_STORES',
        payload: data?.storeSearch?.stores
      });
      dispatch({ type: 'SET_RESERVATION_MODIFY_STORE', payload: store });
    }
  }, [store]);

  useEffect(() => {
    dispatch({ type: 'SET_DAMAGE_OPT_IN_STATUS', payload: damageProtectionEnabled });
  }, [damageProtectionEnabled]);

  return (
    <ReservationContext.Provider value={[state, dispatch]}>
      {children}
    </ReservationContext.Provider>
  );
};

ReservationProvider.propTypes = {
  reservationId: string,
  uuid: string,
  storeId: string,
  children: node.isRequired
};

ReservationProvider.defaultProps = {
  reservationId: '',
  uuid: '',
  storeId: ''
};

ReservationProvider.dataModel = extend(
  GET_RENTAL_FDAV1_MODIFYING_QUERY,
  GET_RENTAL_RESERVATION_QUERY,
  SUBMIT_RENTAL_RESERVATION_MODIFICATION_QUERY,
  GET_RENTAL_FDAV1_QUERY,
  GET_RENTAL_FDAV4_QUERY,
  RentalEstimateWrapperProvider,
  {
    storeSearch: params({ storeSearchInput: stringType().isRequired(), pagesize: stringType() }).shape({
      stores: arrayOf(shapeType({
        storeId: stringType(),
        name: stringType(),
        phone: stringType(),
        toolRentalPhone: stringType(),
        distance: stringType(),
        address: shapeType({
          street: stringType(),
          city: stringType(),
          state: stringType(),
          country: stringType(),
          postalCode: stringType()
        }),
        storeHours: shapeType({
          monday: shapeType({ open: stringType(), close: stringType() }),
          tuesday: shapeType({ open: stringType(), close: stringType() }),
          wednesday: shapeType({ open: stringType(), close: stringType() }),
          thursday: shapeType({ open: stringType(), close: stringType() }),
          friday: shapeType({ open: stringType(), close: stringType() }),
          saturday: shapeType({ open: stringType(), close: stringType() }),
          sunday: shapeType({ open: stringType(), close: stringType() })
        })
      }))
    })
  }
);

export default ReservationProvider;
